import { ApplicantType, BorrowerType } from '@/helpers/const';
import { resolveMarriedPartnerId } from '@/helpers/resolveMarriedPartnerId';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import type { ApplicantWithRelationships, ApplicantId } from '@/types';
import type { BorrowerGroup } from '@/types/BorrowerGroup';
import { computed } from 'vue';

/**
 * Composable for managing applicant permissions in the declaration flow.
 * Handles visibility and editing rules for Income, Expenses, and Assets steps.
 *
 * Two grouping concepts share the same first-come-first-serve / view-only behaviour:
 * - Married/defacto partners (via `applicantRelationships`).
 * - Associated applicants of the same company/trust borrower group (via `borrowerGroups`).
 *   The company/trust income & expense is completed by the first associated applicant;
 *   subsequent associated applicants can view it read-only.
 */
export function useApplicantPermissions() {
  const { authorisedUser, loanApp, submittedFinancials } = useDeclarationProcess();

  const currentUserId = computed(() => authorisedUser.id as ApplicantId);
  const isBrokerUser = computed(() => loanApp.value.userType === 'Broker');

  /**
   * Equity Release secondary declarations show only the signed-in applicant.
   * Bridging still shares a married or de facto partner's financials.
   */
  const isErhlOwnDetailsOnly = computed(() => !isBrokerUser.value && loanApp.value.lendingProduct === 'erhl');

  /**
   * Find the partner of a given applicant using applicantRelationships.
   * Returns the partner if they have a married_or_defacto relationship on this loan.
   */
  const findPartner = (applicantId: ApplicantId): ApplicantWithRelationships | undefined => {
    const partnerId = resolveMarriedPartnerId(applicantId, loanApp.value.applicants);
    return partnerId ? loanApp.value.applicants.find((a) => a.id === partnerId) : undefined;
  };

  const currentUserPartner = computed(() => findPartner(currentUserId.value));

  /**
   * Get the IDs of applicants who share a company/trust borrower group with the given
   * applicant ("associated applicants"). These applicants complete the company/trust
   * income & expense on a first-come-first-serve basis, mirroring how a married couple
   * shares a household section. IDs are returned in `applicants` order for deterministic
   * owner resolution.
   */
  const getCoAssociatedApplicantIds = (applicantId: ApplicantId): ApplicantId[] => {
    const companyTrustGroups = (loanApp.value.borrowerGroups ?? []).filter(
      (group) => group.type === BorrowerType.COMPANY || group.type === BorrowerType.TRUST,
    );

    const coAssociatedIds = new Set<ApplicantId>();
    for (const group of companyTrustGroups) {
      const memberIds = (group.applicants ?? []).map((member) => member.applicantId as ApplicantId);
      if (!memberIds.includes(applicantId)) continue;
      memberIds.forEach((id) => {
        if (id !== applicantId) coAssociatedIds.add(id);
      });
    }

    return loanApp.value.applicants.map((a) => a.id as ApplicantId).filter((id) => coAssociatedIds.has(id));
  };

  const currentUserCoAssociates = computed(() => getCoAssociatedApplicantIds(currentUserId.value));

  /**
   * All applicants who share the current user's income & expense section: a married/defacto
   * partner and/or company/trust co-associated applicants.
   */
  const householdRelatedIds = computed((): ApplicantId[] => {
    const ids = new Set<ApplicantId>();
    if (currentUserPartner.value) ids.add(currentUserPartner.value.id as ApplicantId);
    currentUserCoAssociates.value.forEach((id) => ids.add(id));
    return [...ids];
  });

  /**
   * Whether the current (non-broker) user shares their income & expense section with another
   * applicant — used to decide whether to store under a single shared owner record.
   */
  const isSharingHousehold = computed(() => !isBrokerUser.value && householdRelatedIds.value.length > 0);

  /**
   * Get the related applicants of any given applicant (partner + company/trust co-associates).
   * Used for grouping households in the broker view.
   */
  const getRelatedApplicantIds = (applicantId: ApplicantId): ApplicantId[] => {
    const ids = new Set<ApplicantId>();
    const partner = findPartner(applicantId);
    if (partner) ids.add(partner.id as ApplicantId);
    getCoAssociatedApplicantIds(applicantId).forEach((id) => ids.add(id));
    return [...ids];
  };

  // ============================================================================
  // SUBMISSION STATUS
  // ============================================================================
  // An applicant who has submitted (signed) their declaration has submitted ALL of
  // their sections, so every section is locked — this is the authoritative signal and
  // correctly covers sections submitted empty (e.g. no assets/liabilities), which the
  // financial-content checks below cannot detect. The content checks (read from the
  // entry-time snapshot, not the live editable financials) remain so a first-time
  // submitter can still edit within their session before the declaration is signed.

  const hasDeclared = (applicantId: ApplicantId): boolean =>
    (loanApp.value.declaredApplicantIds ?? []).includes(applicantId);

  const hasSubmittedIncome = (applicantId: ApplicantId): boolean =>
    hasDeclared(applicantId) || (submittedFinancials.value[applicantId]?.income?.employment?.length ?? 0) > 0;

  /**
   * Check if an applicant has already submitted their expenses.
   */
  const hasSubmittedExpenses = (applicantId: ApplicantId): boolean =>
    hasDeclared(applicantId) || submittedFinancials.value[applicantId]?.expenses?.numberOfDependants !== undefined;

  const hasSubmittedAssets = (applicantId: ApplicantId): boolean =>
    hasDeclared(applicantId) ||
    (submittedFinancials.value[applicantId]?.assets?.length ?? 0) > 0 ||
    (submittedFinancials.value[applicantId]?.liabilities?.length ?? 0) > 0;

  // ============================================================================
  // INCOME PERMISSIONS
  // ============================================================================

  /**
   * Get list of applicants visible for the Income step.
   * - Broker: sees all applicants
   * - Single / married to non-applicant: sees own only
   * - Married to another applicant: sees own + partner's
   * - Associated with a company/trust: sees own + co-associated applicants' (read-only)
   */
  const getVisibleApplicantsForIncome = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      return loanApp.value.applicants.map((a) => a.id) as ApplicantId[];
    }

    const visibleIds = new Set<ApplicantId>([currentUserId.value]);
    if (!isErhlOwnDetailsOnly.value) {
      if (currentUserPartner.value) visibleIds.add(currentUserPartner.value.id as ApplicantId);
      currentUserCoAssociates.value.forEach((id) => visibleIds.add(id));
    }

    return [...visibleIds];
  });

  /**
   * Check if current user can edit a specific applicant's income.
   * - Broker: can edit all
   * - Already-submitted income is view-only (locked)
   * - Can edit own income and partner's income (either direction)
   * - Everyone else (not self, not partner) is read-only
   */
  const canEditApplicantIncome = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (isErhlOwnDetailsOnly.value) {
      return applicantId === currentUserId.value && !hasDeclared(applicantId);
    }
    if (hasSubmittedIncome(applicantId)) return false;
    if (applicantId === currentUserId.value) return true;
    if (!isErhlOwnDetailsOnly.value && currentUserPartner.value?.id === applicantId) return true;
    return false;
  };

  /**
   * Get completion status for a specific applicant's income.
   */
  const getApplicantIncomeStatus = (applicantId: ApplicantId) => {
    const applicant = loanApp.value.applicants.find((a) => a.id === applicantId);
    return {
      id: applicantId,
      name: applicant?.preferredName || applicant?.firstName || '',
      isCompleted: (loanApp.value.financials[applicantId]?.income?.employment?.length ?? 0) > 0,
    };
  };

  // ============================================================================
  // EXPENSES PERMISSIONS (Household-based)
  // ============================================================================

  /**
   * Whether an applicant's record actually holds the household's expenses.
   *
   * Deliberately narrower than `hasSubmittedExpenses`, which also returns true once an applicant
   * has declared. A declaration does not move the expense record, so choosing an owner on
   * declaration state switches ownership to a member holding nothing — the UI then reads an empty
   * record, reports the household incomplete, and saves a second one.
   */
  const holdsExpenseRecord = (applicantId: ApplicantId): boolean =>
    submittedFinancials.value[applicantId]?.expenses?.numberOfDependants !== undefined;

  /**
   * Get the household owner for expenses. Ownership follows the data: whichever member already
   * holds the expense record keeps it, then the conventional owner (the primary applicant of a
   * married pair), then loan order. Independent of who is logged in, so every member of a
   * household resolves the same owner and cannot create a second record.
   */
  const getHouseholdExpenseOwnerId = computed((): ApplicantId => {
    if (isErhlOwnDetailsOnly.value) return currentUserId.value;

    // The current user plus everyone sharing their section (partner and/or company/trust
    // co-associates), in loan order so the result does not depend on who is asking.
    const memberIds = loanApp.value.applicants
      .map((a) => a.id as ApplicantId)
      .filter((id) => id === currentUserId.value || householdRelatedIds.value.includes(id));

    const holder = memberIds.find((id) => holdsExpenseRecord(id));
    if (holder) return holder;

    if (currentUserPartner.value) {
      const primaryApplicant = loanApp.value.applicants.find((a) => a.type === ApplicantType.PRIMARY);
      if (primaryApplicant && memberIds.includes(primaryApplicant.id as ApplicantId)) {
        return primaryApplicant.id as ApplicantId;
      }
    }

    return memberIds[0] ?? currentUserId.value;
  });

  /**
   * Check if current user can edit household expenses.
   * - Broker: can always edit
   * - Equity Release: editable until this applicant submits the declaration
   * - Already-submitted household expenses are view-only (locked). This covers both
   *   a single applicant re-opening their own submitted expenses and a partner whose
   *   shared household expenses were already submitted by the other applicant.
   * - Otherwise the current user (first submitter) can edit.
   */
  const canEditHouseholdExpenses = computed((): boolean => {
    if (isBrokerUser.value) return true;
    // Equity Release keeps each applicant's expenses editable until they submit the declaration.
    // A stored dependants count is not a submission.
    if (isErhlOwnDetailsOnly.value) return !hasDeclared(currentUserId.value);
    return !hasSubmittedExpenses(getHouseholdExpenseOwnerId.value);
  });

  /**
   * Get the applicant IDs for expenses display.
   * - Broker: sees one entry per household (married pair or company/trust group)
   * - Regular users: see the household owner
   */
  const getHouseholdApplicantForExpenses = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      const seen = new Set<ApplicantId>();
      const householdIds: ApplicantId[] = [];

      for (const applicant of loanApp.value.applicants) {
        const id = applicant.id as ApplicantId;
        if (seen.has(id)) continue;

        const groupIds = [id, ...getRelatedApplicantIds(id)];
        const primaryId = groupIds.find(
          (gid) => loanApp.value.applicants.find((a) => a.id === gid)?.type === ApplicantType.PRIMARY,
        );
        const ownerId = primaryId ?? id;

        if (!householdIds.includes(ownerId)) householdIds.push(ownerId);
        groupIds.forEach((gid) => seen.add(gid));
      }
      return householdIds;
    }

    return [getHouseholdExpenseOwnerId.value];
  });

  /**
   * Check if current user is viewing a related member's submitted expenses (view-only mode).
   */
  const isViewingPartnerExpenses = computed((): boolean => {
    return householdRelatedIds.value.some((id) => hasSubmittedExpenses(id)) && !canEditHouseholdExpenses.value;
  });

  // ============================================================================
  // COMPANY / TRUST INCOME PERMISSIONS (net profit — first-come-first-serve)
  // ============================================================================

  /** The applicant under whom a company/trust group's net profit is stored (its first member). */
  const getCompanyIncomeOwnerId = (group: BorrowerGroup): ApplicantId | undefined =>
    group.applicants?.[0]?.applicantId as ApplicantId | undefined;

  /** Whether a company/trust group's net profit has been entered. */
  const isCompanyIncomeComplete = (group: BorrowerGroup): boolean => {
    const ownerId = getCompanyIncomeOwnerId(group);
    return !!ownerId && (loanApp.value.financials[ownerId]?.companyIncome?.netProfit?.amount ?? 0) > 0;
  };

  /**
   * Whether the current user can edit a company/trust group's net profit.
   * - Broker: always
   * - Any associated applicant while it is still empty (first-come-first-serve)
   * - Once completed: only the owner; other associated applicants view it read-only
   */
  const canEditCompanyIncome = (group: BorrowerGroup): boolean => {
    if (isBrokerUser.value) return true;
    if (!isCompanyIncomeComplete(group)) return true;
    return getCompanyIncomeOwnerId(group) === currentUserId.value;
  };

  // ============================================================================
  // ASSETS & LIABILITIES PERMISSIONS
  // ============================================================================

  /**
   * Get list of applicants to display in Assets step.
   * - Broker: sees all applicants
   * - Single / married to non-applicant: sees own only
   * - Married to another applicant: sees own + partner's (partner's card without edit button)
   * - Associated with a company/trust: sees own + co-associated applicants' (read-only)
   */
  const getApplicantsForAssetsDisplay = computed((): ApplicantId[] => {
    if (isBrokerUser.value) {
      return loanApp.value.applicants.map((a) => a.id) as ApplicantId[];
    }

    const visibleIds = new Set<ApplicantId>([currentUserId.value]);
    if (!isErhlOwnDetailsOnly.value) {
      if (currentUserPartner.value) visibleIds.add(currentUserPartner.value.id as ApplicantId);
      currentUserCoAssociates.value.forEach((id) => visibleIds.add(id));
    }

    return [...visibleIds];
  });

  /**
   * Check if current user can edit a specific applicant's assets.
   * - Broker: can edit all
   * - Already-submitted assets/liabilities are view-only (locked)
   * - Can edit own and partner's assets/liabilities (either direction)
   * - Everyone else (not self, not partner) is read-only
   */
  const canEditApplicantAssets = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (isErhlOwnDetailsOnly.value) {
      return applicantId === currentUserId.value && !hasDeclared(applicantId);
    }
    if (hasSubmittedAssets(applicantId)) return false;
    if (applicantId === currentUserId.value) return true;
    if (!isErhlOwnDetailsOnly.value && currentUserPartner.value?.id === applicantId) return true;
    return false;
  };

  /**
   * Check if current user can view a specific applicant's assets data.
   * Users can see their own data, their partner's data, and co-associated company/trust
   * applicants' data (read-only).
   */
  const canViewApplicantAssetsData = (applicantId: ApplicantId): boolean => {
    if (isBrokerUser.value) return true;
    if (applicantId === currentUserId.value) return true;
    if (isErhlOwnDetailsOnly.value) return false;
    if (currentUserPartner.value?.id === applicantId) return true;
    if (currentUserCoAssociates.value.includes(applicantId)) return true;
    return false;
  };

  return {
    isBrokerUser,
    currentUserPartner,
    currentUserCoAssociates,
    isSharingHousehold,
    // Income
    getVisibleApplicantsForIncome,
    canEditApplicantIncome,
    getApplicantIncomeStatus,
    // Expenses
    getHouseholdExpenseOwnerId,
    canEditHouseholdExpenses,
    getHouseholdApplicantForExpenses,
    isViewingPartnerExpenses,
    // Assets
    getApplicantsForAssetsDisplay,
    canEditApplicantAssets,
    canViewApplicantAssetsData,
    // Company / Trust income
    isCompanyIncomeComplete,
    canEditCompanyIncome,
  };
}
